import type { FieldValue } from '@fundline/shared/fields/values';
import { merchantSearchPattern } from '@fundline/shared/merchants/search';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

export const PAGE_SIZE = 50;

export interface MerchantListFilters {
  q: string;
  /** Assigned rep (admins only; reps only ever see their own). */
  rep: string | null;
  tag: string | null;
  archived: boolean;
}

export interface MerchantListItem {
  id: string;
  phone: string;
  businessName: string | null;
  ownerName: string | null;
  state: string | null;
  assignedRepId: string;
  archivedAt: string | null;
  tagIds: string[];
}

/** One page of the merchant list. RLS decides which merchants come back. */
export async function fetchMerchantPage(
  workspaceId: string,
  filters: MerchantListFilters,
  offset: number,
): Promise<MerchantListItem[]> {
  const select =
    'id, phone, business_name, owner_name, state, assigned_rep_id, archived_at, created_at, tags:merchant_tags(tag_id)' +
    (filters.tag ? ', tag_filter:merchant_tags!inner(tag_id)' : '');
  let query = supabase().from('merchants').select(select).eq('workspace_id', workspaceId);
  query = filters.archived ? query.not('archived_at', 'is', null) : query.is('archived_at', null);
  if (filters.rep) query = query.eq('assigned_rep_id', filters.rep);
  if (filters.tag) query = query.eq('tag_filter.tag_id', filters.tag);
  const pattern = merchantSearchPattern(filters.q);
  if (pattern) query = query.ilike('search_text', pattern);
  const { data, error } = await query
    .order('created_at', { ascending: false })
    .order('id')
    .range(offset, offset + PAGE_SIZE - 1)
    .overrideTypes<
      Array<{
        id: string;
        phone: string;
        business_name: string | null;
        owner_name: string | null;
        state: string | null;
        assigned_rep_id: string;
        archived_at: string | null;
        tags: Array<{ tag_id: string }>;
      }>,
      { merge: false }
    >();
  if (error) throw new Error(`Couldn't load merchants: ${error.message}`);
  return data.map((m) => ({
    id: m.id,
    phone: m.phone,
    businessName: m.business_name,
    ownerName: m.owner_name,
    state: m.state,
    assignedRepId: m.assigned_rep_id,
    archivedAt: m.archived_at,
    tagIds: m.tags.map((t) => t.tag_id),
  }));
}

export function useMerchantListQuery(workspaceId: string, filters: MerchantListFilters) {
  return useInfiniteQuery<
    MerchantListItem[],
    Error,
    InfiniteData<MerchantListItem[]>,
    ReturnType<typeof queryKeys.merchantList>,
    number
  >({
    queryKey: queryKeys.merchantList(workspaceId, filters),
    queryFn: ({ pageParam }) => fetchMerchantPage(workspaceId, filters, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.length === PAGE_SIZE ? pages.length * PAGE_SIZE : undefined,
  });
}

export interface MerchantFieldValue {
  fieldId: string;
  value: unknown;
  source: string;
  setByUserId: string | null;
  updatedAt: string;
}

export interface MerchantDetails {
  id: string;
  phone: string;
  businessName: string | null;
  ownerName: string | null;
  state: string | null;
  assignedRepId: string;
  archivedAt: string | null;
  createdAt: string;
  source: string;
  /** True once any message exists (from M5); the phone is then read-only. */
  hasMessages: boolean;
  /** Follow the suppression list (M6). */
  optedOutAt: string | null;
  optOutSource: string | null;
  doNotContact: boolean;
  dncReason: string | null;
  /** M7: a manual zone (timezone_source 'manual', set by an admin) or null. */
  timezone: string | null;
  timezoneSource: string;
  values: MerchantFieldValue[];
  tagIds: string[];
}

/** One merchant with its field values and tags, or null when it does not exist or is not visible. */
export async function fetchMerchant(
  workspaceId: string,
  merchantId: string,
): Promise<MerchantDetails | null> {
  const { data, error } = await supabase()
    .from('merchants')
    .select(
      'id, phone, business_name, owner_name, state, assigned_rep_id, archived_at, created_at, source, last_inbound_at, last_outbound_at, last_message_at, opted_out_at, opt_out_source, do_not_contact, dnc_reason, timezone, timezone_source, field_values(field_id, value, source, set_by_user_id, updated_at), merchant_tags(tag_id)',
    )
    .eq('workspace_id', workspaceId)
    .eq('id', merchantId)
    .maybeSingle();
  if (error) throw new Error(`Couldn't load the merchant: ${error.message}`);
  if (!data) return null;
  return {
    id: data.id,
    phone: data.phone,
    businessName: data.business_name,
    ownerName: data.owner_name,
    state: data.state,
    assignedRepId: data.assigned_rep_id,
    archivedAt: data.archived_at,
    createdAt: data.created_at,
    source: data.source,
    hasMessages: Boolean(data.last_inbound_at ?? data.last_outbound_at ?? data.last_message_at),
    optedOutAt: data.opted_out_at,
    optOutSource: data.opt_out_source,
    doNotContact: data.do_not_contact,
    dncReason: data.dnc_reason,
    timezone: data.timezone,
    timezoneSource: data.timezone_source,
    values: data.field_values.map((v) => ({
      fieldId: v.field_id,
      value: v.value,
      source: v.source,
      setByUserId: v.set_by_user_id,
      updatedAt: v.updated_at,
    })),
    tagIds: data.merchant_tags.map((t) => t.tag_id),
  };
}

export function useMerchantQuery(workspaceId: string, merchantId: string) {
  return useQuery({
    queryKey: queryKeys.merchant(workspaceId, merchantId),
    queryFn: () => fetchMerchant(workspaceId, merchantId),
  });
}

export interface HistoryRow {
  id: number;
  fieldId: string;
  oldValue: unknown;
  newValue: unknown;
  source: string;
  setByUserId: string | null;
  createdAt: string;
}

export interface AuditRow {
  id: number;
  action: string;
  actorType: string;
  actorId: string | null;
  data: unknown;
  createdAt: string;
}

/** Raw timeline sources (field history + the merchant's audit rows); merged by buildTimeline. */
export async function fetchTimelineSources(
  workspaceId: string,
  merchantId: string,
): Promise<{ history: HistoryRow[]; audit: AuditRow[] }> {
  const [history, audit] = await Promise.all([
    supabase()
      .from('field_value_history')
      .select('id, field_id, old_value, new_value, source, set_by_user_id, created_at')
      .eq('workspace_id', workspaceId)
      .eq('merchant_id', merchantId)
      .order('created_at', { ascending: false })
      .limit(200),
    supabase()
      .from('audit_log')
      .select('id, action, actor_type, actor_id, data, created_at')
      .eq('workspace_id', workspaceId)
      .eq('merchant_id', merchantId)
      .order('created_at', { ascending: false })
      .limit(200),
  ]);
  if (history.error) throw new Error(`Couldn't load the timeline: ${history.error.message}`);
  if (audit.error) throw new Error(`Couldn't load the timeline: ${audit.error.message}`);
  return {
    history: history.data.map((h) => ({
      id: h.id,
      fieldId: h.field_id,
      oldValue: h.old_value,
      newValue: h.new_value,
      source: h.source,
      setByUserId: h.set_by_user_id,
      createdAt: h.created_at,
    })),
    audit: audit.data.map((a) => ({
      id: a.id,
      action: a.action,
      actorType: a.actor_type,
      actorId: a.actor_id,
      data: a.data,
      createdAt: a.created_at,
    })),
  };
}

export function useTimelineQuery(workspaceId: string, merchantId: string) {
  return useQuery({
    queryKey: queryKeys.merchantTimeline(workspaceId, merchantId),
    queryFn: () => fetchTimelineSources(workspaceId, merchantId),
  });
}

/** Turns a PostgREST error into a sentence for people. */
export function merchantErrorMessage(err: { code?: string; message: string }): string {
  if (err.code === '23505' && err.message.includes('merchants_workspace_id_phone_key')) {
    return 'A merchant with this phone number already exists in this workspace.';
  }
  if (err.code === '42501' && err.message.includes('row-level security')) {
    return 'You are not allowed to make this change.';
  }
  return err.message.charAt(0).toUpperCase() + err.message.slice(1);
}

/** A failed create, with the database code (23505: the number is already in the workspace). */
export class CreateMerchantError extends Error {
  readonly code: string | undefined;

  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = 'CreateMerchantError';
    this.code = code;
  }
}

/** The consent a person records with a new merchant (M8: required, the source chosen by them). */
export interface NewMerchantConsent {
  source: string;
  sourceDetail: string;
  scope: string;
  /** ISO timestamp. */
  capturedAt: string;
  note: string;
}

/** The id of the workspace's merchant with this phone, if the caller can see it (admins: any). */
export async function findMerchantIdByPhone(
  workspaceId: string,
  phone: string,
): Promise<string | null> {
  const { data } = await supabase()
    .from('merchants')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('phone', phone)
    .maybeSingle();
  return data?.id ?? null;
}

export function useCreateMerchant(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      phone: string;
      assignedRepId: string;
      values: Record<string, FieldValue>;
      consent: NewMerchantConsent;
    }) => {
      const { data, error } = await supabase().rpc('create_merchant', {
        p_phone: input.phone,
        p_assigned_rep_id: input.assignedRepId,
        p_values: input.values,
        p_consent: {
          source: input.consent.source,
          source_detail: input.consent.sourceDetail.trim(),
          scope: input.consent.scope.trim(),
          captured_at: input.consent.capturedAt,
          note: input.consent.note.trim(),
        },
      });
      if (error) throw new CreateMerchantError(merchantErrorMessage(error), error.code);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.merchants(workspaceId) }),
  });
}

/** Every change on the profile page: field values, tags, assignment, archive, phone. */
export function useMerchantMutations(workspaceId: string, merchantId: string) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.merchants(workspaceId) });

  const setField = useMutation({
    mutationFn: async (input: { fieldId: string; value: FieldValue | null }) => {
      const { error } = await supabase().rpc('set_merchant_fields', {
        p_merchant_id: merchantId,
        p_values: { [input.fieldId]: input.value },
      });
      if (error) throw new Error(merchantErrorMessage(error));
    },
    onSettled: invalidate,
  });

  const addTag = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase()
        .from('merchant_tags')
        .insert({ workspace_id: workspaceId, merchant_id: merchantId, tag_id: tagId });
      if (error && error.code !== '23505') throw new Error(merchantErrorMessage(error));
    },
    onSettled: invalidate,
  });

  const removeTag = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase()
        .from('merchant_tags')
        .delete()
        .eq('workspace_id', workspaceId)
        .eq('merchant_id', merchantId)
        .eq('tag_id', tagId);
      if (error) throw new Error(merchantErrorMessage(error));
    },
    onSettled: invalidate,
  });

  const update = useMutation({
    mutationFn: async (
      patch: { assigned_rep_id: string } | { archived_at: string | null } | { phone: string },
    ) => {
      const { data, error } = await supabase()
        .from('merchants')
        .update(patch)
        .eq('workspace_id', workspaceId)
        .eq('id', merchantId)
        .select('id');
      if (error) throw new Error(merchantErrorMessage(error));
      if (data.length === 0) throw new Error('You are not allowed to change this merchant.');
    },
    onSettled: invalidate,
  });

  return { setField, addTag, removeTag, update };
}
